import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageSwitcherComponent } from './language-switcher/language-switcher.component';
import { MessageInformationComponent } from './message-information/message-information.component';
import { AuthService } from 'src/services/auth.service';
import { environment } from 'src/environments/environment';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { DateAdapter } from '@angular/material/core';
import { EntiteUtilisateurModel } from 'src/models/selectionEntiteUtilisateurModel';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { InviteUtilisateurDialogComponent } from './tableau-de-bord/utilisateurs-managment/invite-utilisateur-dialog/invite-utilisateur-dialog.component';
import { CreerUtilisateurTenantSSODialogComponent } from './tableau-de-bord/utilisateurs-managment/creer-utilisateur-tenant-sso-dialog/creer-utilisateur-tenant-sso-dialog.component';
import { CreerAdminPiloteTenantSSODialogComponent } from './tableau-de-bord/utilisateurs-managment/creer-admin-pilote-tenant-sso-dialog/creer-admin-pilote-tenant-sso-dialog.component';
import { ERole } from 'src/enum/eRole'; // Import ERole
import { EEntite } from 'src/enum/eEntite';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
    RouterOutlet,
    MatProgressSpinnerModule,
    TranslateModule,
    LanguageSwitcherComponent,
    MessageInformationComponent
]
})
export class AppComponent implements OnInit, OnDestroy {
  SSO_LogoutPage_Uri = environment.SSO_ConnexionPage_Uri + "Account/Login";
  EEntite = EEntite;
  title = 'LMSFrontEnd';
  environment!: string;
  showModeMenu = false;
  isRootMode = false;
  showHeader = true;
  selectedEntiteId!: number;
  isLoadingModeSwitch = false;
  private isRedirecting = false;
  private langChangeSubscription?: Subscription;

  constructor(
    public authService: AuthService,
    public utilisateurService: UtilisateurService,
    private router: Router,
    private translate: TranslateService,
    private dialog: MatDialog
  ) {
    this.environment = environment.name;
  }

  get shouldShowEnvironment(): boolean {
    return !environment.production;
  }

  ngOnInit() {
    const savedLanguage = localStorage.getItem('app-language');
    const languageToUse = savedLanguage || this.translate.currentLang || this.translate.defaultLang || 'fr';

    this.translate.use(languageToUse);
    window.currentLanguage = languageToUse;

    this.langChangeSubscription = this.translate.onLangChange.subscribe(event => {
      window.currentLanguage = event.lang;
      localStorage.setItem('app-language', event.lang);
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.showHeader = !event.url.includes('scormviewer') && !event.url.includes('player');
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isInsideMenu = target.closest('.menu-container');
    if (!isInsideMenu) {
      this.showModeMenu = false;
    }
  }
  returnHome() {
    if (this.isRedirecting) return;
    this.redirectToUtilisateurOrRoot();
  }

  goToProfil() {
    this.showModeMenu = false;
    this.router.navigate(['profil-utilisateur']);
  }
  goToAideEtSupport() {
    this.showModeMenu = false;
    this.router.navigate(['aide-et-soutien']);
  }
  goToTableauDeBord() {
    this.router.navigate(['/tableau-de-bord']);
  }
  goToSelectionEntite() {
    this.showModeMenu = false;
    if (!this.isRootMode) {
      this.router.navigate(['/selection-entite']);
    }
    else {
      this.router.navigate(['/root-selection-entite']);
    }
  }

  goToLibrairie() {
    this.router.navigate(['/accueil']);
  }

  toggleModeMenu() {
    this.showModeMenu = !this.showModeMenu;
  }

  switchMode() {
    this.isLoadingModeSwitch = true;
    this.isRootMode = !this.isRootMode;
    this.utilisateurService.isModeRoot = this.isRootMode;
    this.showModeMenu = false;
    this.redirectToUtilisateurOrRoot();
  }

  showLibrairie(): boolean {
    return this.isRootMode == false &&
      this.utilisateurService.entiteUtilisateurSelectionnee != null &&
      this.utilisateurService.entiteUtilisateurSelectionnee.entiteId != EEntite.ROOT_ENTITE_ID;
  }

  shouldShowAideEtSupport(): boolean {
    return this.utilisateurService.entiteUtilisateurSelectionnee != null;
  }

  shouldShowChangeEntityButton(): boolean {
    return this.isRootMode ||
      (this.utilisateurService.selectionEntiteUtilisateur?.entiteUtilisateurs.length > 1);
  }

  get shouldRestrictMenu(): boolean {
    const entite = this.utilisateurService.entiteUtilisateurSelectionnee;
    return (!entite?.estServiceStudio && !entite?.estServiceStudioContenusPerso) || (entite?.estDemo && entite?.dateFinDemo && new Date(entite.dateFinDemo) < new Date());
  }

  deconnecter() {
    this.showModeMenu = false;
    this.authService.logout();
  }

  canInviteUsers(): boolean {
    const entite = this.utilisateurService.entiteUtilisateurSelectionnee;
    if (!entite || !entite.roleEntites) return false;
    return entite.roleEntites.some(r => r.roleId === ERole.Pilote || r.roleId === ERole.Admin);
  }

  openInviteDialog() {
    this.utilisateurService.obtenirContexte().subscribe({
      next: (res) => {
        this.utilisateurService.contexteUtilisateur = res;
        if (res.aTenantSSO) {
          this.dialog.open(CreerUtilisateurTenantSSODialogComponent, { width: '600px' });

        } else {
          this.dialog.open(InviteUtilisateurDialogComponent, { width: '500px' });
        }
      },
      error: (err) => console.error(err)
    });
  }

  private redirectToUtilisateurOrRoot() {
    this.isRedirecting = true;
    this.utilisateurService.entiteUtilisateurSelectionnee = undefined!;
    if (this.isRootMode) {
      this.utilisateurService.obtenirUtilisateur(true).subscribe({
        next: (res) => {
          this.utilisateurService.selectionEntiteUtilisateur = res;
          this.router.navigate(['root-selection-entite']).then(() => {
            this.isLoadingModeSwitch = false;
            this.isRedirecting = false;
          });
        },
        error: (err) => {
          this.isLoadingModeSwitch = false;
          this.isRedirecting = false;
        }
      });
    } else {
      this.utilisateurService.obtenirUtilisateur(false).subscribe({
        next: (res) => {
          this.utilisateurService.selectionEntiteUtilisateur = res;
          if (this.utilisateurService.selectionEntiteUtilisateur.entiteUtilisateurs.length == 1) {
            this.utilisateurService.entiteUtilisateurSelectionnee =
              this.utilisateurService.selectionEntiteUtilisateur.entiteUtilisateurs[0];
            this.utilisateurService.notifyentiteSelectionChange(
              this.utilisateurService.entiteUtilisateurSelectionnee
            );
            this.router.navigate(['accueil']).then(() => {
              this.isLoadingModeSwitch = false;
              this.isRedirecting = false;
            });
          } else {
            this.router.navigate(['selection-entite']).then(() => {
              this.isLoadingModeSwitch = false;
              this.isRedirecting = false;
            });
          }
        },
        error: (err) => {
          this.isLoadingModeSwitch = false;
          this.isRedirecting = false;
        }
      });
    }
  }

  ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
}
